import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, MapPin, MessageCircle, HeartHandshake, ArrowRight, ArrowDown,
  Target, CheckCircle2, TrendingUp, Shield, BarChart3,
  Award, Phone, Database, Smartphone, Monitor,
  UserCheck, Headphones, PieChart, Layers,
  IndianRupee, Wrench, Handshake, ChevronRight,
  ClipboardList, AlertTriangle, FileText, Zap,
  Globe, Lock, Wifi, Server, Eye
} from 'lucide-react';
import { DashboardDetailOverlay } from '../components/DashboardDetails';

/* ───────────────────────── helpers ───────────────────────── */

const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/* ───────────────────── section nav dots ─────────────────── */
const SECTIONS = [
  'intro', 'video', 'problem', 'team', 'pillars', 'roi',
  'infrastructure', 'crs', 'pricing', 'forward'
];
const SECTION_LABELS = [
  'Intro', 'Video', 'Problem', 'Team', 'Three Pillars', 'Political ROI',
  'IT Infrastructure', 'CRS', 'Pricing', 'Partner'
];

const SideNav = ({ active }) => (
  <nav className="fixed right-6 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-3" data-testid="presentation-sidenav">
    {SECTIONS.map((id, i) => (
      <a
        key={id}
        href={`#${id}`}
        title={SECTION_LABELS[i]}
        className="group flex items-center gap-2 justify-end"
      >
        <span className={`text-[10px] uppercase tracking-widest transition-all opacity-0 group-hover:opacity-100 ${active === i ? 'text-cyan-400 !opacity-100' : 'text-slate-500'}`}>
          {SECTION_LABELS[i]}
        </span>
        <span className={`block rounded-full transition-all duration-300 ${active === i ? 'w-3 h-3 bg-cyan-400 shadow-lg shadow-cyan-500/40' : 'w-2 h-2 bg-slate-600 group-hover:bg-slate-400'}`} />
      </a>
    ))}
  </nav>
);

/* ────────────────── dashboard mock cards ─────────────────── */
const DashboardMock = ({ persona, icon: Icon, color, features, description }) => (
  <div className="bg-slate-800/60 backdrop-blur rounded-2xl border border-slate-700/60 overflow-hidden hover:border-cyan-500/30 transition-all duration-500 group">
    <div className={`h-1.5 bg-gradient-to-r ${color}`} />
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h4 className="font-bold text-white text-sm">{persona}</h4>
          <p className="text-[11px] text-slate-400">{description}</p>
        </div>
      </div>
      <div className="bg-slate-900/80 rounded-xl p-4 space-y-3">
        {features.map((f, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-cyan-400' : i === 1 ? 'bg-emerald-400' : i === 2 ? 'bg-amber-400' : 'bg-purple-400'}`} />
            <div className="flex-1 text-xs text-slate-300">{f}</div>
            <div className="w-16 h-1.5 rounded-full bg-slate-700 overflow-hidden">
              <div className={`h-full rounded-full bg-gradient-to-r ${color}`} style={{ width: `${65 + i * 10}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */

const PresentationPage = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [openDashboard, setOpenDashboard] = useState(null);

  useEffect(() => {
    const handler = () => {
      const scrollY = window.scrollY + window.innerHeight / 3;
      SECTIONS.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el && scrollY >= el.offsetTop) setActiveSection(i);
      });
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="bg-slate-950 text-white min-h-screen selection:bg-cyan-500/30" data-testid="presentation-page">
      <SideNav active={activeSection} />
      {openDashboard && (
        <DashboardDetailOverlay dashboard={openDashboard} onClose={() => setOpenDashboard(null)} />
      )}

      {/* ─── 1. TITLE / INTRO ─── */}
      <section id="intro" className="relative min-h-screen flex items-center px-6 overflow-hidden" data-testid="section-intro">
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-cyan-600/[0.06] rounded-full blur-[120px]" />
        <div className="absolute bottom-10 right-1/4 w-[400px] h-[400px] bg-blue-600/[0.04] rounded-full blur-[100px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />

        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 lg:gap-20 items-center py-24">
          {/* Left — Hook */}
          <div>
            <FadeIn>
              <div className="inline-flex items-center gap-3 mb-8">
                <div className="h-px w-10 bg-gradient-to-r from-transparent to-cyan-500" />
                <span className="text-cyan-400 text-xs font-semibold tracking-[0.25em] uppercase">AEPL Proposal</span>
              </div>
            </FadeIn>

            <FadeIn delay={100}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.08] tracking-tight">
                What if <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">every family</span> remembered <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">your name</span> — every month?
              </h1>
            </FadeIn>

            <FadeIn delay={200}>
              <p className="mt-8 text-base md:text-lg text-slate-400 max-w-xl leading-relaxed">
                Not through rallies or advertisements — but because your initiative delivered something real to their doorstep.
              </p>
            </FadeIn>

            <FadeIn delay={280} className="mt-8">
              <button
                onClick={() => document.getElementById('video')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium transition-colors text-sm"
                data-testid="scroll-begin-btn"
              >
                See How It Works
                <ArrowDown className="w-4 h-4 animate-bounce" />
              </button>
            </FadeIn>
          </div>

          {/* Right — Value prop + Stats */}
          <div className="space-y-8">
            <FadeIn delay={200}>
              <div className="bg-gradient-to-br from-cyan-500/[0.08] to-blue-500/[0.08] border border-cyan-500/20 rounded-xl px-7 py-6">
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  A system that works for you <span className="text-white font-semibold">24/7, 365 days</span> — building voter trust while you focus on governing.
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">
                  <span className="text-cyan-400 font-semibold">Spend less than a single campaign event</span> — and reach 1,00,000+ households every month, build a verified voter database, and create 200+ local jobs.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={360}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { val: '352+', label: 'Gram Panchayats' },
                  { val: '1,00,000+', label: 'Beneficiaries/mo' },
                  { val: '200+', label: 'Women Agents' },
                  { val: '95%', label: 'Fund Accuracy' }
                ].map((s, i) => (
                  <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl px-5 py-4">
                    <div className="text-2xl font-bold text-cyan-400">{s.val}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── 1b. PROJECT VIDEO ─── */}
      <section id="video" className="relative py-28 px-6 overflow-hidden" data-testid="section-video">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute top-20 right-10 w-[400px] h-[400px] bg-cyan-600/[0.04] rounded-full blur-[120px]" />
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-widest uppercase mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                The Project in Action
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                See <span className="text-cyan-400">Gramtek</span> at Work
              </h2>
              <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Watch how our ground team operates — from doorstep delivery to real-time data collection — transforming public welfare across rural India.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-3xl blur-2xl" />
              <div className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-3 md:p-5">
                <div className="relative w-full overflow-hidden rounded-xl" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/-GGQacT7ado?si=vzvAeJB_XV6us9Zt"
                    title="Gramtek Project Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── 2. THE PROBLEM ─── */}
      <section id="problem" className="relative py-28 px-6 overflow-hidden" data-testid="section-problem">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-red-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <AlertTriangle className="w-4 h-4" />
              The Problem
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Public Welfare Has a <span className="text-red-400">Last-Mile Crisis</span>
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-14 leading-relaxed">
              Billions in government funds are allocated every year, yet the intended beneficiaries rarely receive the full benefit. Leakages, lack of accountability, and zero feedback loops plague existing systems.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: IndianRupee, title: 'Fund Leakage', desc: 'Funds allocated for welfare schemes never reach the last mile. No digital trail, no accountability.' },
              { icon: Users, title: 'No Ground Intelligence', desc: 'Leaders have no systematic way to understand what their constituents actually need.' },
              { icon: MessageCircle, title: 'Broken Feedback Loop', desc: 'Complaints get lost in bureaucracy. Constituents feel unheard, trust erodes.' },
              { icon: Database, title: 'No Verified Database', desc: 'No reliable household-level data connecting beneficiaries to their elected representatives.' }
            ].map((item, i) => (
              <FadeIn key={i} delay={200 + i * 80}>
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 hover:border-red-500/20 transition-all duration-300 group">
                  <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center mb-4 group-hover:bg-red-500/20 transition-colors">
                    <item.icon className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 3. THE TEAM (KARYAKARTAS) ─── */}
      <section id="team" className="relative py-28 px-6 overflow-hidden" data-testid="section-team">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-cyan-600/[0.04] rounded-full blur-[100px]" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <Users className="w-4 h-4" />
              The Ground Force
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              The <span className="text-cyan-400">Karyakartas</span> Network
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-14 leading-relaxed">
              Our model is built on a trained network of local women — the Karyakartas. They are not just delivery agents; they become your trusted representatives in every household.
            </p>
          </FadeIn>

          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                role: 'Field Agent (Karyakarta)',
                count: '200+',
                icon: UserCheck,
                color: 'from-cyan-500 to-blue-500',
                points: [
                  'Local women from the same villages',
                  'Trained in app usage & scheme details',
                  'Door-to-door delivery with OTP verification',
                  'Collect household data & sentiment surveys'
                ]
              },
              {
                role: 'Support / Supervisor',
                count: '10+',
                icon: Headphones,
                color: 'from-amber-500 to-orange-500',
                points: [
                  'Manage clusters of 20-30 agents',
                  'Quality control & training',
                  'Escalation point for field issues',
                  'Real-time monitoring via tablet dashboards'
                ]
              },
              {
                role: 'Client Admin / Leader',
                count: 'You',
                icon: Monitor,
                color: 'from-emerald-500 to-teal-500',
                points: [
                  'Full visibility into operations',
                  'Constituency-level analytics',
                  'Complaint resolution oversight',
                  'Direct communication channel to households'
                ]
              }
            ].map((card, i) => (
              <FadeIn key={i} delay={200 + i * 100}>
                <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden hover:border-cyan-500/20 transition-all duration-500 h-full">
                  <div className={`h-1 bg-gradient-to-r ${card.color}`} />
                  <div className="p-7">
                    <div className="flex items-center gap-4 mb-5">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center`}>
                        <card.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{card.role}</h3>
                        <span className="text-xs text-slate-500">{card.count} active</span>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {card.points.map((p, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={500}>
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-6 flex flex-col md:flex-row items-center gap-6">
              <div className="w-14 h-14 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <HeartHandshake className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h4 className="font-bold text-white mb-1">Why Women Agents?</h4>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Women are welcomed into homes more readily, especially when distributing products to female beneficiaries. This creates a trust channel that is impossible to replicate through any other method. Every interaction builds political capital that no advertisement can buy.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── 4. THREE PILLARS ─── */}
      <section id="pillars" className="relative py-28 px-6 overflow-hidden" data-testid="section-pillars">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute bottom-20 left-0 w-[500px] h-[500px] bg-blue-600/[0.04] rounded-full blur-[120px]" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <Layers className="w-4 h-4" />
              The Model
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              The <span className="text-cyan-400">Three Pillars</span> of Gramtek
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-16 leading-relaxed">
              Our model rests on three interconnected pillars. Together, they create a self-reinforcing cycle that grows stronger with every delivery.
            </p>
          </FadeIn>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                num: '01',
                title: 'Direct Contact',
                subtitle: 'Door-to-Door Delivery',
                icon: HeartHandshake,
                colorBg: 'bg-cyan-500/10',
                colorText: 'text-cyan-400',
                colorCheck: 'text-cyan-500',
                desc: "Every delivery is a personal touchpoint. Our Karyakartas hand-deliver products to each household, creating a human connection between the beneficiary and the leader's initiative.",
                outcomes: ['OTP-verified delivery ensures no leakage', 'Builds a verified household database', 'Creates monthly touchpoints with 1,00,000+ beneficiaries']
              },
              {
                num: '02',
                title: 'Data Collection',
                subtitle: 'Real-Time Ground Intelligence',
                icon: BarChart3,
                colorBg: 'bg-emerald-500/10',
                colorText: 'text-emerald-400',
                colorCheck: 'text-emerald-500',
                desc: 'During every delivery, agents collect structured sentiment data — what issues matter most, which government schemes are reaching people, and what gaps exist.',
                outcomes: ['Monthly pulse on constituency health', 'Issue identification before they escalate', 'Data-driven decision making for leaders']
              },
              {
                num: '03',
                title: 'Scheme Linkage',
                subtitle: 'Connecting People to Benefits',
                icon: Target,
                colorBg: 'bg-amber-500/10',
                colorText: 'text-amber-400',
                colorCheck: 'text-amber-500',
                desc: 'Using the data collected, we help connect eligible beneficiaries to government schemes they may not know about — turning passive distribution into active empowerment.',
                outcomes: ['Identify eligible but unreached beneficiaries', 'Direct scheme enrollment assistance', 'Measurable impact on welfare delivery']
              }
            ].map((pillar, i) => (
              <FadeIn key={i} delay={200 + i * 120}>
                <div className="relative bg-slate-900/60 border border-slate-800 rounded-2xl p-8 hover:border-cyan-500/20 transition-all duration-500 group h-full">
                  <div className={`absolute top-6 right-6 text-5xl font-black opacity-10 group-hover:opacity-20 transition-opacity ${pillar.colorText}`}>{pillar.num}</div>
                  <div className={`w-12 h-12 rounded-xl ${pillar.colorBg} flex items-center justify-center mb-5`}>
                    <pillar.icon className={`w-6 h-6 ${pillar.colorText}`} />
                  </div>
                  <div className={`text-xs ${pillar.colorText} font-semibold uppercase tracking-wider mb-2`}>{pillar.subtitle}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{pillar.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed mb-6">{pillar.desc}</p>
                  <div className="space-y-2">
                    {pillar.outcomes.map((o, j) => (
                      <div key={j} className="flex items-start gap-2 text-xs text-slate-300">
                        <CheckCircle2 className={`w-3.5 h-3.5 ${pillar.colorCheck} flex-shrink-0 mt-0.5`} />
                        {o}
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={600}>
            <div className="mt-14 bg-slate-900/40 border border-slate-800 rounded-2xl p-8 text-center">
              <p className="text-sm text-slate-400 mb-4">The Self-Reinforcing Cycle</p>
              <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-medium">
                <span className="bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-lg">Delivery</span>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-lg">Data</span>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="bg-amber-500/10 text-amber-400 px-4 py-2 rounded-lg">Scheme Linkage</span>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="bg-purple-500/10 text-purple-400 px-4 py-2 rounded-lg">Trust & Goodwill</span>
                <ChevronRight className="w-4 h-4 text-slate-600" />
                <span className="bg-cyan-500/10 text-cyan-400 px-4 py-2 rounded-lg border border-cyan-500/30">Repeat</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── 5. POLITICAL ROI ─── */}
      <section id="roi" className="relative py-28 px-6 overflow-hidden" data-testid="section-roi">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute top-20 right-10 w-[400px] h-[400px] bg-emerald-600/[0.04] rounded-full blur-[120px]" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <TrendingUp className="w-4 h-4" />
              Why This Matters
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              The <span className="text-emerald-400">Political ROI</span>
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-14 leading-relaxed">
              This isn't just a service delivery mechanism. It's the most effective constituency engagement tool available — creating genuine goodwill that compounds over time.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              {
                metric: '1,00,000+',
                label: 'Monthly Household Touchpoints',
                desc: "Every month, your name is associated with a tangible benefit delivered directly to a family's doorstep.",
                icon: HeartHandshake
              },
              {
                metric: '3,50,000+',
                label: 'Verified Voter Database',
                desc: 'A household-level database built through trusted interactions — not purchased lists or estimates.',
                icon: Database
              },
              {
                metric: 'Real-Time',
                label: 'Constituency Pulse',
                desc: 'Know what issues matter most — water, electricity, roads, healthcare — before they become crises.',
                icon: BarChart3
              },
              {
                metric: '200+',
                label: 'Women Employed',
                desc: 'Direct employment creation in rural areas — a visible and personal impact on local livelihoods.',
                icon: Users
              }
            ].map((item, i) => (
              <FadeIn key={i} delay={200 + i * 80}>
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 hover:border-emerald-500/20 transition-all duration-300 flex gap-5">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-400 mb-0.5">{item.metric}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{item.label}</div>
                    <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={600}>
            <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-8">
              <blockquote className="text-lg md:text-xl text-white font-medium italic leading-relaxed mb-3">
                "Every interaction between a beneficiary and your representative builds political capital that no advertisement can buy."
              </blockquote>
              <p className="text-sm text-emerald-400">— The AEPL Philosophy</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── 6. IT INFRASTRUCTURE ─── */}
      <section id="infrastructure" className="relative py-28 px-6 overflow-hidden" data-testid="section-infrastructure">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute bottom-20 left-10 w-[500px] h-[500px] bg-cyan-600/[0.04] rounded-full blur-[120px]" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <Server className="w-4 h-4" />
              Technology Platform
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              IT <span className="text-cyan-400">Infrastructure</span>
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-8 leading-relaxed">
              Four purpose-built dashboards for four distinct user personas. Each interface is designed for its specific user — from agents with basic smartphones to leaders needing high-level analytics.
            </p>
          </FadeIn>

          <FadeIn delay={200}>
            <div className="flex flex-wrap gap-3 mb-14">
              {[
                { icon: Smartphone, label: 'Mobile-First' },
                { icon: Wifi, label: 'Offline Capable' },
                { icon: Lock, label: 'OTP Secured' },
                { icon: Globe, label: 'Multi-Language' },
                { icon: Zap, label: 'Real-Time Sync' }
              ].map((b, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-2 text-xs text-slate-300">
                  <b.icon className="w-3.5 h-3.5 text-cyan-500" />
                  {b.label}
                </span>
              ))}
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { key: 'agent', persona: 'Agent Dashboard', icon: Smartphone, color: 'from-cyan-500 to-blue-500', description: 'Mobile app for field Karyakartas', features: ['Daily delivery route & beneficiary list', 'OTP-verified product handoff', 'Household sentiment survey forms', 'Offline mode with auto-sync'] },
              { key: 'support', persona: 'Support Dashboard', icon: Headphones, color: 'from-amber-500 to-orange-500', description: 'Tablet interface for supervisors', features: ['Agent performance & coverage tracking', 'Real-time delivery progress by village', 'Escalation management & issue alerts', 'Training material & SOPs library'] },
              { key: 'client', persona: 'Leader Dashboard', icon: Monitor, color: 'from-emerald-500 to-teal-500', description: 'Analytics portal for leaders', features: ['Constituency-level delivery analytics', 'Sentiment heatmaps by village', 'Scheme coverage & impact reports', 'Complaint resolution analytics'] }
            ].map((d, i) => (
              <FadeIn key={d.key} delay={250 + i * 100}>
                <button
                  onClick={() => setOpenDashboard(d.key)}
                  className="w-full text-left bg-slate-800/60 backdrop-blur rounded-2xl border border-slate-700/60 overflow-hidden hover:border-cyan-500/40 transition-all duration-500 group cursor-pointer"
                  data-testid={`dashboard-card-${d.key}`}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${d.color}`} />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${d.color} flex items-center justify-center`}>
                        <d.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-white text-sm">{d.persona}</h4>
                        <p className="text-[11px] text-slate-400">{d.description}</p>
                      </div>
                      <div className="w-8 h-8 rounded-lg bg-slate-700/50 group-hover:bg-cyan-500/20 flex items-center justify-center transition-colors">
                        <Eye className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 transition-colors" />
                      </div>
                    </div>
                    <div className="bg-slate-900/80 rounded-xl p-4 space-y-3">
                      {d.features.map((f, j) => (
                        <div key={j} className="flex items-center gap-3">
                          <div className={`w-1.5 h-1.5 rounded-full ${j === 0 ? 'bg-cyan-400' : j === 1 ? 'bg-emerald-400' : j === 2 ? 'bg-amber-400' : 'bg-purple-400'}`} />
                          <div className="flex-1 text-xs text-slate-300">{f}</div>
                          <div className="w-16 h-1.5 rounded-full bg-slate-700 overflow-hidden">
                            <div className={`h-full rounded-full bg-gradient-to-r ${d.color}`} style={{ width: `${65 + j * 10}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-cyan-400/70 mt-3 group-hover:text-cyan-400 transition-colors text-center">Click to view detailed demo</p>
                  </div>
                </button>
              </FadeIn>
            ))}
            <FadeIn delay={550}>
              <DashboardMock
                persona="Beneficiary Portal"
                icon={UserCheck}
                color="from-purple-500 to-violet-500"
                description="Simple view for beneficiaries"
                features={[
                  'Delivery history & receipts',
                  'Scheme eligibility checker',
                  'Direct complaint registration',
                  'Upcoming delivery schedule'
                ]}
              />
            </FadeIn>
          </div>

          <FadeIn delay={650}>
            <div className="mt-14 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-3xl blur-2xl" />
              <div className="relative bg-slate-900/80 border border-slate-800 rounded-2xl p-4 md:p-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_content-hub-780/artifacts/nitbdgm3_Shots%20Mockups%20%283%29.png"
                  alt="Gramtek Mobile App — Agent Dashboard with delivery tracking, OTP verification, and beneficiary management"
                  className="w-full rounded-xl"
                />
                <p className="text-center text-xs text-slate-500 mt-4">Gramtek Agent App — Designed for simplicity. Built for rural India.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── 7. CRS BRIEF ─── */}
      <section id="crs" className="relative py-28 px-6 overflow-hidden" data-testid="section-crs">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-amber-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <ClipboardList className="w-4 h-4" />
              Integrated Module
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Complaint <span className="text-amber-400">Resolution System</span>
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-14 leading-relaxed">
              An integrated ticketing and escalation system that ensures no complaint goes unanswered. Powered by Zoho Desk, it brings corporate-grade issue management to constituency governance.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            <FadeIn delay={200}>
              <div className="space-y-5">
                {[
                  { icon: FileText, title: 'Ticket Creation', desc: 'Complaints registered via app, WhatsApp, or phone call — all tracked under one system.' },
                  { icon: Layers, title: 'Auto-Escalation', desc: 'Unresolved issues automatically escalate up the chain based on configurable SLA timers.' },
                  { icon: PieChart, title: 'Resolution Analytics', desc: 'Track resolution rates, average response times, and common issue categories by geography.' },
                  { icon: Phone, title: 'WhatsApp Integration', desc: 'Beneficiaries can lodge and track complaints via WhatsApp — no app download needed.' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 group">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/20 transition-colors">
                      <item.icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-1 text-sm">{item.title}</h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>

            <FadeIn delay={350}>
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                <h4 className="text-sm font-bold text-white mb-5">How It Works</h4>
                <div className="space-y-4">
                  {[
                    { step: '1', label: 'Complaint Received', detail: 'Via app, WhatsApp, or phone' },
                    { step: '2', label: 'Ticket Created', detail: 'Auto-categorized & assigned' },
                    { step: '3', label: 'Resolution in Progress', detail: 'Tracked with SLA timers' },
                    { step: '4', label: 'Closed & Verified', detail: 'Beneficiary confirmation' }
                  ].map((s, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold flex-shrink-0">
                        {s.step}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white">{s.label}</div>
                        <div className="text-xs text-slate-500">{s.detail}</div>
                      </div>
                      {i < 3 && <ChevronRight className="w-4 h-4 text-slate-700" />}
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[
                    { val: '85%', label: 'Resolution Rate' },
                    { val: '48 hrs', label: 'Avg Response' },
                    { val: '10K+', label: 'Issues Resolved' }
                  ].map((m, i) => (
                    <div key={i} className="bg-slate-800/60 rounded-lg p-3 text-center">
                      <div className="text-lg font-bold text-amber-400">{m.val}</div>
                      <div className="text-[10px] text-slate-500">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── 8. PRICING ─── */}
      <section id="pricing" className="relative py-28 px-6 overflow-hidden" data-testid="section-pricing">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute top-40 left-1/4 w-[500px] h-[500px] bg-cyan-600/[0.04] rounded-full blur-[120px]" />
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <IndianRupee className="w-4 h-4" />
              Investment
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Transparent <span className="text-cyan-400">Pricing</span>
            </h2>
          </FadeIn>

          <FadeIn delay={150}>
            <p className="text-slate-400 text-base md:text-lg max-w-3xl mb-14 leading-relaxed">
              A clear, tiered model designed for scalability. Your technology costs decrease as your impact increases.
            </p>
          </FadeIn>

          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {/* Card 1: Onboarding */}
            <FadeIn delay={200}>
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all duration-500 h-full flex flex-col">
                <div className="h-1 bg-gradient-to-r from-cyan-500 to-blue-500" />
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">Initial Setup & Onboarding</h3>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">First Month</span>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">
                    <span className="text-lg text-slate-500">&#8377;</span> 5,00,000
                  </div>
                  <p className="text-xs text-slate-500 mb-6">One-time onboarding fee</p>
                  <ul className="space-y-3 flex-1">
                    {[
                      'Complete software deployment & operational onboarding',
                      'WhatsApp integration for OTPs, notifications & communication',
                      'Zoho Desk setup for support & ticketing',
                      'Agent & beneficiary communication channels',
                      'Full team training & go-live support'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-300 mt-4 leading-relaxed bg-slate-800/60 rounded-lg px-3 py-2">
                    * Zoho Desk subscription/licensing charges billed as per actuals
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* Card 2: Volume Slabs */}
            <FadeIn delay={300}>
              <div className="bg-slate-900/60 border-2 border-cyan-500/40 rounded-2xl overflow-hidden relative h-full flex flex-col">
                <div className="absolute top-4 right-4 bg-cyan-500 text-slate-950 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                  Scales With You
                </div>
                <div className="h-1 bg-gradient-to-r from-cyan-400 to-emerald-400" />
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">Volume-Based Delivery Slabs</h3>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">Ongoing Software Usage</span>
                    </div>
                  </div>

                  <div className="space-y-5 flex-1">
                    {[
                      { slab: 'Up to 50,000 Deliveries', cost: '2,00,000', per: '4.00' },
                      { slab: 'Up to 1,00,000 Deliveries', cost: '3,00,000', per: '3.00' },
                      { slab: 'Up to 1,50,000 Deliveries', cost: '4,00,000', per: '2.66' }
                    ].map((tier, i) => (
                      <div key={i} className="bg-slate-800/60 rounded-xl p-5">
                        <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{tier.slab}</div>
                        <div className="text-3xl font-bold text-white mb-1">
                          <span className="text-lg text-slate-500">&#8377;</span> {tier.cost}
                          <span className="text-base text-slate-500 font-normal"> / month</span>
                        </div>
                        <div className="text-xs text-emerald-400 font-medium">
                          &#8377; {tier.per} per delivery
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-slate-400 mt-6 leading-relaxed">
                    As your delivery volume scales, technology overheads become increasingly cost-efficient.
                  </p>
                </div>
              </div>
            </FadeIn>

            {/* Card 3: Custom Dev */}
            <FadeIn delay={400}>
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all duration-500 h-full flex flex-col">
                <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
                <div className="p-7 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-sm">Custom Feature Development</h3>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider">As Needed</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Starting from</div>
                  <div className="text-3xl font-bold text-white mb-1">
                    <span className="text-lg text-slate-500">&#8377;</span> 35,000
                    <span className="text-base text-slate-500 font-normal"> / week</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">Scoped & approved before work begins</p>
                  <ul className="space-y-3 flex-1">
                    {[
                      'Advanced analytics & custom reporting',
                      'Regional language additions',
                      'Specific hardware integrations',
                      'New module development',
                      'API integrations with government systems'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                    We always scope out the time required for any custom request and get your approval before beginning work.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── 9. MOVING FORWARD ─── */}
      <section id="forward" className="relative py-28 px-6 overflow-hidden" data-testid="section-forward">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-cyan-600/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-blue-600/[0.04] rounded-full blur-[100px]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <FadeIn>
            <div className="inline-flex items-center gap-2 text-cyan-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <Handshake className="w-4 h-4" />
              Partnership
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Moving <span className="text-cyan-400">Forward Together</span>
            </h2>
          </FadeIn>

          <FadeIn delay={200}>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              We view this as a long-term partnership rather than a standard vendor relationship. We want to ensure this pricing aligns with your budgetary goals and operational plans.
            </p>
          </FadeIn>

          <FadeIn delay={300}>
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-8 mb-12 text-left">
              <p className="text-white text-lg font-medium italic leading-relaxed text-center">
                "We are completely open to sitting down and negotiating these terms to find a sweet spot that makes this partnership a reality."
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={400}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25"
                data-testid="cta-schedule-discussion"
              >
                Schedule a Discussion
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/products/pds"
                className="inline-flex items-center justify-center gap-2 border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-4 rounded-lg transition-all duration-300"
                data-testid="cta-explore-gramtek"
              >
                Explore Gramtek-PDS
              </Link>
            </div>
          </FadeIn>

          <FadeIn delay={500}>
            <p className="mt-8 text-slate-600 text-xs">
              AEPL &middot; Technology for Governance &middot; Confidential
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default PresentationPage;
